import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { api } from "../api/client.js";
import { resolveProductMediaUrl } from "../data/products.js";
import {
  formatMinorAmount,
  stripeConfigured,
  stripePromise,
} from "../config/stripe.js";
import {
  getApiFieldErrors,
  getRequestErrorMessage,
} from "../Utils/formValidation.js";
import { validatePurchaseDetails } from "../Utils/purchaseValidation.js";
import BuildOrderPaymentForm from "./BuildOrderPaymentForm.js";
import MockUpsShippingStep from "./MockUpsShippingStep.js";
import "../Style/PurchaseModal.css";
import "../Style/BuildOrderModal.css";

const INITIAL_CUSTOMER = { fullName: "", email: "", phone: "" };
const INITIAL_BILLING = {
  country: "US",
  state: "",
  city: "",
  zip: "",
  line1: "",
  line2: "",
};
const BALLOON_MAX_QUANTITY = 15;
const CANDLE_MAX_QUANTITY = 200;
const DEFAULT_MAX_QUANTITY = 200;

const itemKey = (itemType, id) => `${itemType}:${id}`;

const getItemId = (key) => key.slice(key.indexOf(":") + 1);

const getQuantity = (quantities, itemType, id) =>
  quantities[itemKey(itemType, id)] || 0;

const getDisplayName = (item) =>
  item.name || item.fragranceNote || item.scent || "Untitled";

const isCandleCatalogProduct = (product) => product.productType === "candle";

const getProductMaxQuantity = (product) =>
  product?.productType === "balloon"
    ? BALLOON_MAX_QUANTITY
    : DEFAULT_MAX_QUANTITY;

const clampQuantity = (value, minQuantity, maxQuantity) => {
  const quantity = Number(value);
  if (!Number.isFinite(quantity)) return minQuantity;
  return Math.min(maxQuantity, Math.max(minQuantity, Math.trunc(quantity)));
};

function BuildOrderField({
  label,
  name,
  value,
  onChange,
  error,
  ...inputProps
}) {
  const errorId = `build-order-${name.replace(".", "-")}-error`;
  return (
    <label className="purchase-field">
      <span>{label}</span>
      <input
        name={name}
        value={value}
        onChange={onChange}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        {...inputProps}
      />
      {error && (
        <small id={errorId} className="form-field-error">
          {error}
        </small>
      )}
    </label>
  );
}

function QuantityControl({
  label,
  value,
  maxQuantity,
  disabled = false,
  onChange,
}) {
  return (
    <div className="purchase-quantity-stack build-order-quantity-stack">
      <div className="purchase-quantity build-order-quantity">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 1))}
          disabled={disabled || value === 0}
          aria-label={`Decrease ${label}`}
        >
          -
        </button>
        <input
          type="number"
          min="0"
          max={maxQuantity}
          inputMode="numeric"
          value={value}
          disabled={disabled}
          onChange={({ target }) =>
            onChange(clampQuantity(target.value, 0, maxQuantity))
          }
          aria-label={`${label} quantity`}
        />
        <button
          type="button"
          onClick={() => onChange(Math.min(maxQuantity, value + 1))}
          disabled={disabled || value === maxQuantity}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
      {value === maxQuantity && (
        <small className="quantity-limit-note">
          For greater quantities, contact us.
        </small>
      )}
    </div>
  );
}

function BuildOrderProductCard({ product, quantity, onQuantityChange }) {
  const imageUrl = resolveProductMediaUrl(product.imageUrl || product.image);
  const maxQuantity = getProductMaxQuantity(product);
  const isOutOfStock = product.inStock === false;

  return (
    <article
      className={`build-order-item-card${isOutOfStock ? " is-out-of-stock" : ""}`}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={product.alt || getDisplayName(product)} />
      ) : (
        <span className="build-order-item-placeholder" aria-hidden="true" />
      )}
      <div className="build-order-item-body">
        <div>
          <p>The Collection</p>
          <h3>{getDisplayName(product)}</h3>
          {isOutOfStock && <small>Out of stock</small>}
          <small>{product.mood}</small>
        </div>
        <QuantityControl
          label={getDisplayName(product)}
          value={quantity}
          maxQuantity={maxQuantity}
          disabled={isOutOfStock}
          onChange={onQuantityChange}
        />
      </div>
    </article>
  );
}

function BuildOrderCandleCard({ candle, quantity, onQuantityChange }) {
  const name = getDisplayName(candle);
  const isOutOfStock = candle.inStock === false;

  return (
    <article
      className={`build-order-item-card build-order-candle-card${
        isOutOfStock ? " is-out-of-stock" : ""
      }`}
    >
      <span className="build-order-candle-swatch" aria-hidden="true">
        <i style={{ backgroundColor: candle.color?.value }} />
      </span>
      <div className="build-order-item-body">
        <div>
          <p>Candle set</p>
          <h3>{name}</h3>
          {isOutOfStock && <small>Out of stock</small>}
          <small>{candle.mood}</small>
        </div>
        <QuantityControl
          label={name}
          value={quantity}
          maxQuantity={CANDLE_MAX_QUANTITY}
          disabled={isOutOfStock}
          onChange={onQuantityChange}
        />
      </div>
    </article>
  );
}

export default function BuildOrderModal({
  initialProductId = "",
  returnPath = "/contact-us",
  onClose,
}) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const paymentBusyRef = useRef(false);
  const [products, setProducts] = useState([]);
  const [candles, setCandles] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [initialSelectionApplied, setInitialSelectionApplied] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [step, setStep] = useState(1);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [customer, setCustomer] = useState(INITIAL_CUSTOMER);
  const [billingAddress, setBillingAddress] = useState(INITIAL_BILLING);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [initializing, setInitializing] = useState(false);
  const [payment, setPayment] = useState(null);
  const [result, setResult] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState(null);

  const requestClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    closeRef.current = requestClose;
  }, [requestClose]);

  useEffect(() => {
    paymentBusyRef.current = paymentBusy;
  }, [paymentBusy]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !paymentBusyRef.current) {
        closeRef.current();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setCatalogLoading(true);
    setCatalogError("");

    Promise.all([
      api.products.list(
        { page: 1, limit: 100, featured: true },
        { signal: controller.signal },
      ),
      api.candles.list(undefined, { signal: controller.signal }),
    ])
      .then(([productsResponse, candlesResponse]) => {
        setProducts(
          (productsResponse.data || []).filter((product) => product.featured),
        );
        setCandles(candlesResponse.data || []);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setCatalogError(
            getRequestErrorMessage(
              requestError,
              "The order catalog could not be loaded.",
            ),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setCatalogLoading(false);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (initialSelectionApplied || !initialProductId || products.length === 0) {
      return;
    }

    if (
      products.some(
        (product) =>
          product.id === initialProductId && product.inStock !== false,
      )
    ) {
      setQuantities((current) => ({
        ...current,
        [itemKey("product", initialProductId)]:
          current[itemKey("product", initialProductId)] || 1,
      }));
    }
    setInitialSelectionApplied(true);
  }, [initialProductId, initialSelectionApplied, products]);

  const selectedItems = useMemo(
    () =>
      Object.entries(quantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([key, quantity]) => {
          const itemType = key.startsWith("candle:") ? "candle" : "product";
          const id = getItemId(key);

          return itemType === "candle"
            ? { itemType, candleId: id, quantity }
            : { itemType, productId: id, quantity };
        }),
    [quantities],
  );

  const selectedCount = selectedItems.reduce(
    (count, item) => count + item.quantity,
    0,
  );
  const summaryLabel =
    selectedCount === 1
      ? "Build order (1 item)"
      : `Build order (${selectedCount} items)`;

  useEffect(() => {
    const controller = new AbortController();

    if (selectedItems.length === 0) {
      setQuote(null);
      setQuoteError("");
      setQuoteLoading(false);
      return undefined;
    }

    setQuoteLoading(true);
    setQuoteError("");
    api.buildOrders
      .quote(selectedItems, { signal: controller.signal })
      .then((response) => setQuote(response.data))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setQuote(null);
          setQuoteError(
            getRequestErrorMessage(
              requestError,
              "The order total could not be loaded.",
            ),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setQuoteLoading(false);
      });

    return () => controller.abort();
  }, [selectedItems]);

  const updateQuantity = (itemType, id, quantity) => {
    setQuantities((current) => {
      const key = itemKey(itemType, id);
      const next = { ...current };

      if (quantity <= 0) {
        delete next[key];
      } else {
        next[key] = quantity;
      }

      return next;
    });
    setError("");
  };

  const handleDetailsChange = ({ target: { name, value } }) => {
    const [group, field] = name.split(".");
    if (group === "customer") {
      setCustomer((current) => ({ ...current, [field]: value }));
    } else {
      setBillingAddress((current) => ({ ...current, [field]: value }));
    }
    setFieldErrors((current) => ({ ...current, [name]: "" }));
    setError("");
  };

  const applySuggestedAddress = (address) => {
    setBillingAddress((current) => ({ ...current, ...address }));
    setFieldErrors((current) => ({
      ...current,
      "billingAddress.country": "",
      "billingAddress.state": "",
      "billingAddress.city": "",
      "billingAddress.zip": "",
      "billingAddress.line1": "",
      "billingAddress.line2": "",
    }));
    setError("");
  };

  const continueToBilling = () => {
    if (selectedItems.length === 0) {
      setError("Please select at least one item.");
      return;
    }
    if (quoteError) {
      setError(quoteError);
      return;
    }
    setError("");
    setStep(2);
  };

  const continueToPayment = async (event) => {
    event.preventDefault();
    if (initializing) return;

    const validationErrors = validatePurchaseDetails(customer, billingAddress);
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setError("Please correct the highlighted billing details.");
      return;
    }
    if (!stripeConfigured) {
      setError("Payments are not configured for this website.");
      return;
    }
    if (!selectedShipping?.serviceCode) {
      setError("Please choose a UPS delivery option.");
      return;
    }

    setInitializing(true);
    setError("");
    try {
      const response = await api.buildOrders.createIntent({
        items: selectedItems,
        customer: {
          fullName: customer.fullName.trim(),
          email: customer.email.trim(),
          phone: customer.phone.trim(),
        },
        billingAddress: {
          country: billingAddress.country.trim().toUpperCase(),
          state: billingAddress.state.trim(),
          city: billingAddress.city.trim(),
          zip: billingAddress.zip.trim(),
          line1: billingAddress.line1.trim(),
          line2: billingAddress.line2.trim(),
        },
        shipping: {
          address: {
            country: billingAddress.country.trim().toUpperCase(),
            state: billingAddress.state.trim(),
            city: billingAddress.city.trim(),
            zip: billingAddress.zip.trim(),
            line1: billingAddress.line1.trim(),
            line2: billingAddress.line2.trim(),
            residential: true,
          },
          selectedRate: selectedShipping,
        },
      });
      setPayment(response.data);
      setQuote({
        items: response.data.items,
        itemCount: response.data.items.reduce(
          (count, item) => count + item.quantity,
          0,
        ),
        totalAmount: response.data.amount,
        currency: response.data.currency,
      });
      setStep(3);
    } catch (requestError) {
      const apiErrors = getApiFieldErrors(requestError);
      setFieldErrors(apiErrors);
      setError(
        Object.keys(apiErrors).length
          ? "Please correct the highlighted billing details."
          : getRequestErrorMessage(
              requestError,
              "Payment could not be initialized. Please try again.",
            ),
      );
    } finally {
      setInitializing(false);
    }
  };

  const canClose = !paymentBusy;
  const title =
    result?.status === "succeeded"
      ? "Payment successful"
      : result?.status === "processing"
        ? "Payment processing"
        : step === 1
          ? "Build your order"
          : step === 2
            ? "Shipping information"
            : "Secure payment";
  const balloonProducts = products.filter(
    (product) => !isCandleCatalogProduct(product),
  );
  const candleProducts = products.filter(isCandleCatalogProduct);
  const renderProductSection = (items, titleId, titleText) => (
    <section className="build-order-section" aria-labelledby={titleId}>
      <h3 id={titleId}>{titleText}</h3>
      <div className="build-order-item-grid">
        {items.map((product) => (
          <BuildOrderProductCard
            key={product.id}
            product={product}
            quantity={getQuantity(quantities, "product", product.id)}
            onQuantityChange={(quantity) =>
              updateQuantity("product", product.id, quantity)
            }
          />
        ))}
      </div>
    </section>
  );

  return (
    <div
      className="purchase-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && canClose) requestClose();
      }}
    >
      <section
        className="purchase-modal build-order-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="build-order-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="purchase-header">
          <div>
            <p>Up &amp; Away</p>
            <h2 id="build-order-title">{title}</h2>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={!canClose}
            aria-label="Close build order"
          >
            x
          </button>
        </header>

        {!result && (
          <ol className="purchase-steps" aria-label="Build order progress">
            {["Order", "Shipping", "Payment"].map((label, index) => (
              <li className={step >= index + 1 ? "active" : ""} key={label}>
                <span>{index + 1}</span>
                {label}
              </li>
            ))}
          </ol>
        )}

        {result ? (
          <div className={`purchase-result is-${result.status}`} role="status">
            <span aria-hidden="true">
              {result.status === "succeeded" ? "OK" : "..."}
            </span>
            <h3>
              {result.status === "succeeded"
                ? "Your order is confirmed."
                : "Your payment is processing."}
            </h3>
            <p>
              {result.status === "succeeded"
                ? `Thank you, ${customer.fullName}. Your order reference is ${result.order.id}.`
                : "We will update the order automatically when Stripe confirms the payment."}
            </p>
            <button
              type="button"
              className="purchase-primary"
              onClick={requestClose}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {step === 1 && (
              <div className="purchase-review build-order-review">
                {catalogLoading && (
                  <p className="purchase-loading" role="status">
                    Loading catalog...
                  </p>
                )}

                {!catalogLoading && catalogError && (
                  <p className="purchase-error" role="alert">
                    {catalogError}
                  </p>
                )}

                {!catalogLoading && !catalogError && (
                  <>
                    {renderProductSection(
                      balloonProducts,
                      "build-order-balloon-products-title",
                      "Balloon products",
                    )}

                    {renderProductSection(
                      candleProducts,
                      "build-order-candle-products-title",
                      "Candle set products",
                    )}

                    <section
                      className="build-order-section"
                      aria-labelledby="build-order-candles-title"
                    >
                      <h3 id="build-order-candles-title">Candle sets</h3>
                      <div className="build-order-item-grid">
                        {candles.map((candle) => (
                          <BuildOrderCandleCard
                            key={candle.id}
                            candle={candle}
                            quantity={getQuantity(
                              quantities,
                              "candle",
                              candle.id,
                            )}
                            onQuantityChange={(quantity) =>
                              updateQuantity("candle", candle.id, quantity)
                            }
                          />
                        ))}
                      </div>
                    </section>
                  </>
                )}

                <div className="purchase-total">
                  <span>Total</span>
                  <strong>
                    {quoteLoading
                      ? "Calculating..."
                      : quote
                        ? formatMinorAmount(quote.totalAmount, quote.currency)
                        : "Unavailable"}
                  </strong>
                </div>

                {(error || quoteError) && (
                  <p className="purchase-error" role="alert">
                    {error || quoteError}
                  </p>
                )}

                <button
                  type="button"
                  className="purchase-primary"
                  onClick={continueToBilling}
                  disabled={
                    catalogLoading ||
                    Boolean(catalogError) ||
                    quoteLoading ||
                    !quote ||
                    selectedItems.length === 0
                  }
                >
                  Continue
                </button>
              </div>
            )}

            {step === 2 && (
              <MockUpsShippingStep
                customer={customer}
                billingAddress={billingAddress}
                fieldErrors={fieldErrors}
                error={error}
                initializing={initializing}
                productAmount={quote?.totalAmount || 0}
                currency={quote?.currency || "USD"}
                summaryLabel={summaryLabel}
                items={selectedItems}
                onChange={handleDetailsChange}
                onApplySuggestedAddress={applySuggestedAddress}
                onShippingSelected={setSelectedShipping}
                onBack={() => {
                  setError("");
                  setSelectedShipping(null);
                  setStep(1);
                }}
                onSubmit={continueToPayment}
              />
            )}

            {false && step === 2 && (
              <form
                className="purchase-details-form"
                onSubmit={continueToPayment}
                noValidate
              >
                <div className="build-order-summary">
                  <span>{summaryLabel}</span>
                  <strong>
                    {quote
                      ? formatMinorAmount(quote.totalAmount, quote.currency)
                      : "Unavailable"}
                  </strong>
                </div>

                <div className="purchase-field-grid">
                  <BuildOrderField
                    label="Full name *"
                    name="customer.fullName"
                    value={customer.fullName}
                    onChange={handleDetailsChange}
                    error={fieldErrors["customer.fullName"]}
                    autoComplete="name"
                  />
                  <BuildOrderField
                    label="Email *"
                    name="customer.email"
                    value={customer.email}
                    onChange={handleDetailsChange}
                    error={fieldErrors["customer.email"]}
                    type="email"
                    autoComplete="email"
                  />
                  <BuildOrderField
                    label="Phone *"
                    name="customer.phone"
                    value={customer.phone}
                    onChange={handleDetailsChange}
                    error={fieldErrors["customer.phone"]}
                    type="tel"
                    autoComplete="tel"
                  />
                  <BuildOrderField
                    label="Country code *"
                    name="billingAddress.country"
                    value={billingAddress.country}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.country"]}
                    placeholder="LB"
                    maxLength={2}
                    autoComplete="country"
                  />
                  <BuildOrderField
                    label="State / Province *"
                    name="billingAddress.state"
                    value={billingAddress.state}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.state"]}
                    autoComplete="address-level1"
                  />
                  <BuildOrderField
                    label="City *"
                    name="billingAddress.city"
                    value={billingAddress.city}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.city"]}
                    autoComplete="address-level2"
                  />
                  <BuildOrderField
                    label="ZIP / Postal code *"
                    name="billingAddress.zip"
                    value={billingAddress.zip}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.zip"]}
                    autoComplete="postal-code"
                  />
                  <BuildOrderField
                    label="Address line 1 *"
                    name="billingAddress.line1"
                    value={billingAddress.line1}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.line1"]}
                    autoComplete="address-line1"
                  />
                  <BuildOrderField
                    label="Address line 2"
                    name="billingAddress.line2"
                    value={billingAddress.line2}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.line2"]}
                    autoComplete="address-line2"
                  />
                </div>

                {error && (
                  <p className="purchase-error" role="alert">
                    {error}
                  </p>
                )}

                <div className="purchase-actions">
                  <button
                    type="button"
                    className="purchase-secondary"
                    onClick={() => {
                      setError("");
                      setStep(1);
                    }}
                    disabled={initializing}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="purchase-primary"
                    disabled={initializing}
                  >
                    {initializing
                      ? "Preparing secure payment..."
                      : "Continue to payment"}
                  </button>
                </div>
              </form>
            )}

            {step === 3 && payment?.clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: payment.clientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary: "#a8873a",
                      colorText: "#1c1913",
                      colorBackground: "#fffefa",
                      borderRadius: "0px",
                      fontFamily: "Lora, serif",
                    },
                  },
                }}
              >
                <BuildOrderPaymentForm
                  summaryLabel={summaryLabel}
                  customer={customer}
                  billingAddress={billingAddress}
                  clientSecret={payment.clientSecret}
                  paymentIntentId={payment.paymentIntentId}
                  amount={payment.amount}
                  currency={payment.currency}
                  onBusyChange={setPaymentBusy}
                  returnPath={returnPath}
                  onSuccess={(order) =>
                    setResult({ status: "succeeded", order })
                  }
                  onProcessing={(order) =>
                    setResult({ status: "processing", order })
                  }
                />
              </Elements>
            )}
          </>
        )}
      </section>
    </div>
  );
}
